import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Starfield } from "../components/Starfield";
import { DiamondMark } from "../components/DiamondMark";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Auth() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, setUser } = useAuth();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user.user_id) navigate("/app");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const path = mode === "register" ? "/auth/register" : "/auth/login";
      const body = mode === "register" ? form : { email: form.email, password: form.password };
      const { data } = await api.post(path, body);
      setUser(data);
      toast.success(mode === "register" ? "Welcome to ClearGlass" : "Welcome back");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative min-h-screen bg-cg-bg flex items-center justify-center px-5 py-16">
      <Starfield count={60} />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-md rounded-3xl cg-glass p-8 shadow-[0_8px_60px_rgba(7,8,20,0.7)]"
      >
        <button onClick={() => navigate("/")} className="flex items-center gap-2.5 mb-8 mx-auto">
          <DiamondMark size={30} />
          <span className="font-serif text-xl tracking-tight">ClearGlass<span className="text-cg-cyan">.ai</span></span>
        </button>

        <h1 className="font-serif text-2xl font-light text-center mb-1">
          {mode === "register" ? "Create your account" : "Welcome back"}
        </h1>
        <p className="text-sm text-cg-textm text-center mb-7">
          {mode === "register" ? "Begin your ascension — 14-day free trial" : "Sign in to continue your sessions"}
        </p>

        <Button
          data-testid="google-signin-button"
          onClick={googleSignIn}
          variant="outline"
          className="w-full h-11 rounded-full bg-white/95 hover:bg-white text-[#1D1540] border-0 font-medium"
        >
          <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
          Continue with Google
        </Button>

        <div className="flex items-center gap-3 my-6">
          <div className="h-px flex-1 bg-[rgba(140,79,214,0.25)]" />
          <span className="text-xs text-cg-textm font-mono uppercase tracking-widest">or</span>
          <div className="h-px flex-1 bg-[rgba(140,79,214,0.25)]" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div>
              <Label className="text-cg-texts text-sm">Name</Label>
              <Input
                data-testid="auth-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                placeholder="Your name"
                className="mt-1.5 h-11 bg-cg-overlay/60 border-[rgba(140,79,214,0.3)] text-cg-textp placeholder:text-cg-textm focus-visible:ring-cg-cyan"
              />
            </div>
          )}
          <div>
            <Label className="text-cg-texts text-sm">Email</Label>
            <Input
              data-testid="auth-email-input"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              placeholder="you@example.com"
              className="mt-1.5 h-11 bg-cg-overlay/60 border-[rgba(140,79,214,0.3)] text-cg-textp placeholder:text-cg-textm focus-visible:ring-cg-cyan"
            />
          </div>
          <div>
            <Label className="text-cg-texts text-sm">Password</Label>
            <Input
              data-testid="auth-password-input"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={6}
              placeholder="••••••••"
              className="mt-1.5 h-11 bg-cg-overlay/60 border-[rgba(140,79,214,0.3)] text-cg-textp placeholder:text-cg-textm focus-visible:ring-cg-cyan"
            />
          </div>
          <Button
            data-testid="auth-submit-button"
            type="submit"
            disabled={loading}
            className="w-full h-11 rounded-full bg-cg-violet hover:bg-cg-violetglow text-white shadow-[0_0_20px_rgba(109,53,192,0.4)]"
          >
            {loading ? "Please wait…" : mode === "register" ? "Create account" : "Sign in"}
          </Button>
        </form>

        <p className="text-sm text-cg-textm text-center mt-6">
          {mode === "register" ? "Already have an account?" : "New to ClearGlass?"}{" "}
          <button
            data-testid="auth-toggle-mode"
            onClick={() => setMode(mode === "register" ? "login" : "register")}
            className="text-cg-cyan hover:underline"
          >
            {mode === "register" ? "Sign in" : "Create one"}
          </button>
        </p>
      </motion.div>
    </div>
  );
}
